import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import isValid from 'date-fns/isValid'
import { Button, useMediaQuery, Modal, showToast } from '@viveaesthetics/shared'
import { WithDataTestId } from '@viveaesthetics/elemy-shared-core/lib/components/types'
import {
  ButtonSize,
  Variant as ButtonVariant
} from '@viveaesthetics/elemy-shared-core/lib/components/types/Button'
import { useFeatureFlags } from '@viveaesthetics/elemy-shared-core/lib/store/featureFlags'

import { useGetDomainByAppName } from '@/hooks/useGetDomainByAppName'
import { useParams } from '@/hooks/useParams'
import { StatesEnum } from '@/types/providers'
import { useAppContext } from '@/context/PreloadAppContext'

import {
  ProviderBookButtonForm,
  ProviderBookButtonFormDefault,
  Steps
} from './ProviderBookButton.types'
import { TimeSlotStep } from './TimeSlotStep/TimeSlotStep'
import { EmailStep } from './EmailStep/EmailStep'
import { providerBookButtonFormValidation } from './ProviderBookButton.const'
import { useProviderBookButtonSubmit } from './ProviderBookButton.hooks'

interface ProviderBookButtonProps extends WithDataTestId {
  provider: {
    id: string
    name: string
    imgSrc: string
  }
  state: StatesEnum
  defaultValues?: ProviderBookButtonFormDefault
  onClose?: () => void
  variant?: ButtonVariant
  buttonText?: string
  variantSize?: ButtonSize
}

const steps = [Steps.TimeSlot, Steps.Email]

const getDefaultStep = (defaultValues?: ProviderBookButtonFormDefault) => {
  let currentStep: Steps = steps[0]

  if (defaultValues?.timeSlot) {
    currentStep = steps[1]
  }

  return currentStep
}

export const ProviderBookButton = ({
  provider,
  state,
  defaultValues = {},
  onClose,
  'data-test-id': dataTestId = 'book-session-button',
  variant,
  buttonText = 'Book session',
  variantSize = 'medium'
}: ProviderBookButtonProps) => {
  const flags = useFeatureFlags()
  const { branding } = useAppContext()
  const { isMobile } = useMediaQuery()
  const [open, setModalOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState(getDefaultStep(defaultValues))
  const { domain, isLoading: isDomainLoading } = useGetDomainByAppName({
    applicationName: 'CLIENT_360'
  })

  const { email: providedEmail, selectProvider } = useParams<{
    email: string
    selectProvider: string
  }>()

  const { redirectBasedOnPublicClientInfo } = useProviderBookButtonSubmit({
    providerId: provider.id,
    domain: domain || ''
  })

  const formDefaultValues: ProviderBookButtonForm = {
    timeSlot:
      defaultValues.timeSlot && isValid(new Date(defaultValues.timeSlot))
        ? new Date(defaultValues.timeSlot)
        : undefined,
    email: defaultValues.email || providedEmail || ''
  }

  const {
    control,
    handleSubmit,
    errors,
    setValue,
    getValues,
    formState: { isSubmitting }
  } = useForm<ProviderBookButtonForm>({
    shouldUnregister: false,
    defaultValues: formDefaultValues,
    resolver: providerBookButtonFormValidation
  })

  const setValuesToDefault = () => {
    setValue('timeSlot', formDefaultValues.timeSlot)
    setValue('email', formDefaultValues.email)
    setCurrentStep(getDefaultStep(defaultValues))
  }

  useEffect(() => {
    setValuesToDefault()
  }, Object.values(defaultValues))

  const onSubmit = handleSubmit(data => {
    if (data.timeSlot && data.email) {
      return redirectBasedOnPublicClientInfo({
        timeSlot: data.timeSlot,
        email: data.email,
        state
      })
    } else {
      showToast({
        heading: 'Unexpected error',
        text: 'Some form fields are not filled in'
      })
    }

    return null
  })

  const onModalClose = () => {
    if (onClose) onClose()
    setValuesToDefault()
    setModalOpen(false)
  }

  const navigateStepFrom = (step: Steps) => {
    const idx = steps.findIndex(n => n === step) + 1
    setCurrentStep(steps[idx] || steps[0])
  }

  const stepsItems = {
    [Steps.TimeSlot]: {
      value: (
        <TimeSlotStep
          control={control}
          provider={provider}
          error={errors?.timeSlot?.message}
          onChange={date => {
            if (
              providedEmail &&
              selectProvider &&
              flags['pedp-3859-schedule-with-another-provider']
            ) {
              redirectBasedOnPublicClientInfo({
                timeSlot: date,
                email: providedEmail,
                state
              })
              return
            }
            setValue('timeSlot', date)
            navigateStepFrom(Steps.TimeSlot)
          }}
        />
      ),
      continueOnChange: true,
      formFields: ['timeSlot']
    },
    [Steps.Email]: {
      value: (
        <EmailStep
          provider={provider}
          control={control}
          onSubmit={onSubmit}
          error={errors?.email?.message}
          timeSlot={getValues().timeSlot}
          isLoading={isSubmitting}
        />
      ),
      continueOnChange: false,
      formFields: ['email']
    }
  } as const

  return (
    <>
      <Button
        data-test-id={dataTestId}
        variantSize={variantSize}
        fullWidth={isMobile}
        onClick={() => {
          setModalOpen(true)
        }}
        variant={variant}
        loading={isDomainLoading}
      >
        {buttonText}
      </Button>
      <Modal
        data-test-id="book-session-modal"
        open={open}
        onClose={onModalClose}
        variant="v2"
        backgroundImage={branding.images.modalBg}
        padding={currentStep === steps[1] ? 'spacing0' : undefined}
        onBack={
          currentStep !== steps[0]
            ? () => {
                const prevStep = steps.findIndex(s => s === currentStep) - 1
                const newStepKey = steps[prevStep] || steps[0]
                const newStep = stepsItems[newStepKey]

                if (newStep?.continueOnChange) {
                  newStep?.formFields.forEach(field =>
                    setValue(field, undefined)
                  )
                  setCurrentStep(newStepKey)
                }
              }
            : undefined
        }
      >
        {stepsItems[currentStep]?.value}
      </Modal>
    </>
  )
}
